/**
 * SubjectCareerMapModal.jsx - 3D Enhanced + Pop-out Animation
 */

import { useEffect, useRef, useState, useMemo } from "react";
import {
  FaCompass,
} from "react-icons/fa";

/* ─── Easing ─────────────────────────────────── */
function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/* ─── Layout ─────────────────────────────────── */
const W = 1280;
const H = 1250;
const CX = W / 2;
const CY = H / 2;
const CENTER_R = 92;
const JOB_R = 82;           // ← Increased size
const BASE_ORBIT_R = 405;   // ← Slightly shorter connectors

const PALETTE = {
  bg: "#0c1322",
  bgCard: "rgba(10,20,46,0.97)",
  border: "rgba(54,167,248,0.45)",
  centerFill: "#ffaa00",
  centerText: "#fff",
  jobFill: "#36a7f8",
  jobText: "#e0f4ff",
  link: "rgba(54,167,248,0.42)",
  linkHover: "#00e1ff",
  glow: "rgba(0,225,255,0.28)",
};

function wrapWords(text, maxChars = 14) {
  const words = (text || "").split(" ");
  const lines = [];
  let current = "";
  for (const w of words) {
    if ((current + " " + w).trim().length <= maxChars) {
      current = (current + " " + w).trim();
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function layoutNodes(count) {
  if (count === 0) return [];
  
  return Array.from({ length: count }, (_, i) => {
    let angle = (2 * Math.PI * i) / count - Math.PI / 2;
    angle += (Math.sin(i * 1.7) * 0.13);

    const radiusVariation = Math.sin(i * 3) * 158 + Math.cos(i * 1.5) * 52;
    const finalRadius = BASE_ORBIT_R + radiusVariation;
    
    const ySquash = 0.87 + Math.sin(i * 0.8) * 0.05;

    return { 
      finalX: CX + finalRadius * Math.cos(angle),
      finalY: CY + finalRadius * Math.sin(angle) * ySquash,
      angle,
      finalRadius
    };
  });
}

export default function SubjectCareerMapModal({
  subject,
  jobposts = [],
  loading = false,
  onClose,
  onJobClick,
}) {
  const [hovered, setHovered] = useState(null);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    startRef.current = performance.now();
    const DURATION = 650;

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(elapsed / DURATION, 1);
      setProgress(easeOutBack(t));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const targetPositions = useMemo(() => layoutNodes(jobposts.length), [jobposts.length]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 12000, backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          background: PALETTE.bgCard,
          border: `none`,
          borderRadius: 24,
          boxShadow: `0 40px 120px rgba(0,0,0,0.85), 0 0 100px ${PALETTE.glow}`,
          padding: "32px 36px 28px",
          maxWidth: "min(96vw, 1350px)",
          width: "100%",
          position: "relative",
          backgroundColor: "rgba(2, 0, 29, 0)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
  <FaCompass 
    size={15} 
    color="#ffaa00" 
    style={{ marginRight: "10px", verticalAlign: "middle" }}
  />
  <span style={{ 
    margin: 0, 
    fontSize: "0.82rem", 
    color: "#ffaa00", 
    letterSpacing: "0.1em", 
    fontWeight: 600,
    verticalAlign: "middle"
  }}>
    Compass
  </span>
  <h3 style={{ margin: "4px 0 0", color: "white", fontSize: "1.65rem", fontWeight: 700 }}>
    {subject}
  </h3>
</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#88a", fontSize: "2.2rem", cursor: "pointer", padding: "0 8px" }}>
            ×
          </button>
        </div>

        <svg 
          viewBox={`0 0 ${W} ${H}`} 
          width="100%" 
          style={{ display: "block", maxHeight: "85vh" }}
        >
          <defs>
            <radialGradient id="centerGrad" cx="35%" cy="32%" r="68%">
              <stop offset="0%" stopColor="#ffe58874" />
              <stop offset="48%" stopColor="#ffaa0068" />
              <stop offset="100%" stopColor="#d46a0063" />
            </radialGradient>

            <radialGradient id="jobGrad" cx="37%" cy="33%" r="70%">
              <stop offset="0%" stopColor="#00b7ff6b" />
              <stop offset="48%" stopColor="#0277d06c" />
              <stop offset="100%" stopColor="#013a7069" />
            </radialGradient>

            <filter id="shadow3d" x="-70%" y="-70%" width="240%" height="240%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
              <feOffset dx="6" dy="9" result="offsetblur" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.65" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="glow" x="-90%" y="-90%" width="280%" height="280%">
              <feGaussianBlur stdDeviation="12" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Animated Links - Slightly Shorter */}
          {!loading && jobposts.map((job, i) => {
            const target = targetPositions[i];
            if (!target) return null;
            const isHov = hovered === job.jobpost_id;

            const x = CX + (target.finalX - CX) * progress;
            const y = CY + (target.finalY - CY) * progress;

            return (
              <line
                key={`link-${job.jobpost_id}`}
                x1={CX} y1={CY}
                x2={x} y2={y}
                stroke={isHov ? PALETTE.linkHover : PALETTE.link}
                strokeWidth={isHov ? 4.5 : 2.8}
                strokeLinecap="round"
              />
            );
          })}

          {/* Larger Satellite Nodes */}
          {!loading && jobposts.map((job, i) => {
            const target = targetPositions[i];
            if (!target) return null;
            const isHov = hovered === job.jobpost_id;

            const x = CX + (target.finalX - CX) * progress;
            const y = CY + (target.finalY - CY) * progress;

            const lines = wrapWords(job.title, 13);
            const lineH = 15;
            const textY = y - ((lines.length - 1) * lineH) / 2 + 2;

            return (
              <g
                key={`node-${job.jobpost_id}`}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHovered(job.jobpost_id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => { onJobClick(job); onClose(); }}
              >
                {/* Shadow */}
                <circle
                  cx={x + 7} 
                  cy={y + 9} 
                  r={JOB_R}
                  fill="#00000055"
                  filter="url(#shadow3d)"
                />

                <circle
                  cx={x} 
                  cy={y} 
                  r={JOB_R}
                  fill="url(#jobGrad)"
                  stroke={isHov ? "#00f8ff" : "#1e95e0"}
                  strokeWidth={isHov ? 5.5 : 3.5}
                  filter={isHov ? "url(#glow)" : undefined}
                  style={{
                    transition: "stroke 0.2s ease",
                    transform: isHov ? "scale(1.11)" : "scale(1)",
                    transformOrigin: `${x}px ${y}px`
                  }}
                />

                {lines.map((line, li) => (
                  <text
                    key={li}
                    x={x} 
                    y={textY + li * lineH}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={isHov ? "#ffffff" : PALETTE.jobText}
                    fontSize="15"                    // Slightly larger text
                    fontWeight={isHov ? 700 : 500}
                    style={{ pointerEvents: "none" }}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          })}

          {/* Center Node */}
          {!loading && (
            <g>
              <circle 
                cx={CX + 8} 
                cy={CY + 10} 
                r={CENTER_R} 
                fill="#00000066" 
                filter="url(#shadow3d)" 
              />

              <circle
                cx={CX} 
                cy={CY} 
                r={CENTER_R}
                fill="url(#centerGrad)"
                stroke="#ffe38a"
                strokeWidth="6"
                filter="url(#glow)"
              />

              {wrapWords(subject, 13).map((line, li, arr) => (
                <text
                  key={li}
                  x={CX} 
                  y={CY - ((arr.length - 1) * 15) / 2 + li * 15 + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={PALETTE.centerText}
                  fontSize="20"
                  fontWeight="700"
                  style={{ pointerEvents: "none" }}
                >
                  {line}
                </text>
              ))}
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}